import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IBudget, IBudgetVersionService } from "../../../../models/budgetV2";
import { Occurrence } from "../../../../models/occurrence";
import { ITruckerPayPlanData } from "../../../../models/truckerPayPlan";
import { Asset } from "../../../../models/asset";
import { createBudgetV2, fetchBudgetUserProfile } from "../../../../api/fetchBudgetV2";
import {
  fetchOccurrence,
  fetchOccurrenceCustomer,
  fetchOccurrenceVehicle,
  IOccurrenceSummary,
} from "../../../../api/fetchOccurence";
import { fetchContractDetails } from "../../../../api/fetchContractDetails";
import { BudgetRecipient } from "../../enums/budgetRecipient";
import { buildBudgetRequestFromBudget } from "../../budgetEdit/utils/buildBudgetRequestFromBudget";
import {
  createEmptyBudgetVersionService,
  createEmptyBudgetVersionServiceItem,
} from "../../budgetEdit/utils/emptyBudgetVersionService";
import { createEmptyBudget } from "../utils/createEmptyBudget";
import {
  applyOccurrenceDataToBudget,
  resolveOccurrenceUserRole,
} from "../utils/occurrenceBudgetMapper";

export interface IUseBudgetCreateOverrides {
  fixedOccurrenceId?: string;
  onCreated?: () => void;
}

export const useBudgetCreate = (persona: string, overrides?: IUseBudgetCreateOverrides) => {
  const navigate = useNavigate();

  const [budget, setBudget] = useState<IBudget>(createEmptyBudget());
  const [occurrence, setOccurrence] = useState<Occurrence | null>(null);
  const [occurrenceId, setOccurrenceId] = useState<string | undefined>(overrides?.fixedOccurrenceId);
  const [truckerPayPlan, setTruckerPayPlan] = useState<ITruckerPayPlanData | null>(null);
  const [profile, setProfile] = useState<BudgetRecipient | undefined>();
  const [loadingOccurrence, setLoadingOccurrence] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      const profileFetched = await fetchBudgetUserProfile();
      if (profileFetched) setProfile(profileFetched);
    };
    loadProfile();
  }, []);

  useEffect(() => {
    const loadOccurrenceData = async () => {
      if (!occurrenceId || !profile) {
        setOccurrence(null);
        setTruckerPayPlan(null);
        return;
      }

      setLoadingOccurrence(true);
      try {
        const [occurrenceResult, vehicleData, customerData] = await Promise.all([
          fetchOccurrence(occurrenceId, resolveOccurrenceUserRole(profile)),
          fetchOccurrenceVehicle(occurrenceId),
          fetchOccurrenceCustomer(occurrenceId),
        ]);

        if (occurrenceResult) setOccurrence(occurrenceResult);
        setBudget((prev) => applyOccurrenceDataToBudget(prev, occurrenceResult, vehicleData, customerData));
      } finally {
        setLoadingOccurrence(false);
      }
    };

    loadOccurrenceData();
  }, [occurrenceId, profile]);

  useEffect(() => {
    const loadTruckerPayPlan = async () => {
      if (!occurrence?.vehicleChassis) {
        setTruckerPayPlan(null);
        return;
      }

      const response = await fetchContractDetails({
        chassis: occurrence.vehicleChassis || "",
        odometer: occurrence.vehicleOdometer || 0,
        hourmeter: occurrence.vehicleHourmeter || 0,
      } as Asset);

      setTruckerPayPlan(response ?? null);
    };

    loadTruckerPayPlan();
  }, [occurrence?.vehicleChassis, occurrence?.vehicleOdometer, occurrence?.vehicleHourmeter]);

  const setServices = (services: IBudgetVersionService[]) => {
    setBudget((prev) => ({ ...prev, versionData: { ...prev.versionData, services } }));
  };

  const addEmptyService = () => {
    setBudget((prev) => {
      const currentServices = prev.versionData?.services ?? [];
      return {
        ...prev,
        versionData: {
          ...prev.versionData,
          services: [...currentServices, createEmptyBudgetVersionService()],
        },
      };
    });
  };

  const addEmptyItemToService = (serviceIndex: number) => {
    setBudget((prev) => {
      const currentServices = prev.versionData?.services ?? [];
      const services = currentServices.map((service, index) => {
        if (index !== serviceIndex) return service;
        const currentItems = service.items ?? service.serviceItems ?? [];
        const nextRef = `${serviceIndex + 1}.${String(currentItems.length + 1).padStart(2, "0")}`;
        const nextItems = [...currentItems, createEmptyBudgetVersionServiceItem(nextRef)];
        return { ...service, items: nextItems, serviceItems: nextItems };
      });
      return { ...prev, versionData: { ...prev.versionData, services } };
    });
  };

  const selectOccurrence = (summary: IOccurrenceSummary) => setOccurrenceId(summary.uuid);

  const clearOccurrence = () => {
    setOccurrenceId(undefined);
    setOccurrence(null);
    setTruckerPayPlan(null);
    setBudget(createEmptyBudget());
  };

  const canSendBudget = useMemo(() => {
    if (!occurrenceId) return false;
    const services = budget.versionData?.services ?? [];
    if (services.length === 0) return false;
    return services.every((service) => {
      const items = service.items?.length ? service.items : service.serviceItems ?? [];
      return items.some((item) => item.removed !== true && (item.quantity ?? 0) > 0);
    });
  }, [occurrenceId, budget.versionData?.services]);

  const createBudget = async () => {
    if (!occurrenceId || !canSendBudget) return;
    setSubmitting(true);
    try {
      await createBudgetV2(buildBudgetRequestFromBudget(budget, occurrenceId));
      if (overrides?.onCreated) {
        overrides.onCreated();
      } else {
        navigate(`/${persona}/budgets`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return {
    budget,
    occurrence,
    truckerPayPlan,
    profile,
    loadingOccurrence,
    submitting,
    canSendBudget,
    occurrenceId,
    setBudget,
    setServices,
    addEmptyService,
    addEmptyItemToService,
    selectOccurrence,
    clearOccurrence,
    createBudget,
  };
};
